import type { AnalyticsExportRequestRow } from '@/lib/analytics-export-request'

export type AnalyticsExportErrorStateCode =
  | 'none_mock_safe'
  | 'export_failed_safe'
  | 'provider_unavailable'

export type AnalyticsExportErrorStateSurface =
  | 'analytics_export_panel'
  | 'export_job_worker'
  | 'signed_url_delivery'

export type AnalyticsExportErrorStateRetryPolicy =
  | 'manual_retry_allowed'
  | 'retry_after_provider_ready'
  | 'no_retry_needed'

export type AnalyticsExportErrorStateAuditState =
  | 'not_persisted_mock_safe'
  | 'pending_d1_audit_history'

export type AnalyticsExportErrorStatePreviewInput = {
  errorCode: AnalyticsExportErrorStateCode
  errorSurface: AnalyticsExportErrorStateSurface
  retryPolicy: AnalyticsExportErrorStateRetryPolicy
  providerState: string
  auditState: AnalyticsExportErrorStateAuditState
  safeMessage: string
}

export type AnalyticsExportErrorStatePreviewPayload = AnalyticsExportErrorStatePreviewInput

export type AnalyticsExportErrorStatePreview = {
  payload: AnalyticsExportErrorStatePreviewPayload
  rows: AnalyticsExportRequestRow[]
  mockSafe: true
}

const forbiddenErrorStatePreviewKeys = new Set([
  'answer',
  'answers',
  'rawanswer',
  'rawanswers',
  'sample',
  'samples',
  'rawsample',
  'rawsamples',
  'content',
  'row',
  'rows',
  'rawrow',
  'rawrows',
  'errorrow',
  'errorrows',
  'rawerror',
  'rawerrors',
  'email',
  'contactemail',
  'phone',
  'contactphone',
  'mobile',
  'address',
  'companyaddress',
  'taxid',
  'tax_id',
  'companytaxid',
  'businessaccountingno',
  'providerpayload',
  'd1rows',
  'rawd1rows',
])

const unsafeErrorStateValuePatterns = [
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
  /09\d{8}/,
  /0\d{1,2}-?\d{6,8}(?:#?\d+)?/,
  /[\u4e00-\u9fff]{0,8}(市|縣).{0,24}(路|街|巷|弄|號)/,
  /(^|[^\d])\d{8}([^\d]|$)/,
]

export function buildAnalyticsExportErrorStatePreview(
  input: AnalyticsExportErrorStatePreviewInput,
): AnalyticsExportErrorStatePreview {
  assertSafeAnalyticsExportErrorStatePreview(input)

  return {
    mockSafe: true,
    payload: {
      errorCode: input.errorCode,
      errorSurface: input.errorSurface,
      retryPolicy: input.retryPolicy,
      providerState: input.providerState,
      auditState: input.auditState,
      safeMessage: input.safeMessage,
    },
    rows: [
      { field: 'errorCode', value: getErrorCodeLabel(input.errorCode) },
      { field: 'errorSurface', value: getErrorSurfaceLabel(input.errorSurface) },
      { field: 'retryPolicy', value: getRetryPolicyLabel(input.retryPolicy) },
      { field: 'providerState', value: getProviderStateLabel(input.providerState) },
      { field: 'auditState', value: getAuditStateLabel(input.auditState) },
      { field: 'safeMessage', value: input.safeMessage },
      {
        field: 'mock-safe 說明',
        value: 'error state preview，不查 D1，不呼叫 provider，不寫入匯出檔',
      },
    ],
  }
}

function getErrorCodeLabel(errorCode: AnalyticsExportErrorStateCode) {
  if (errorCode === 'export_failed_safe') return 'export_failed_safe（匯出失敗安全代碼）'
  if (errorCode === 'provider_unavailable') return 'provider_unavailable（正式 provider 不可用）'
  return 'none_mock_safe（目前沒有錯誤）'
}

function getErrorSurfaceLabel(errorSurface: AnalyticsExportErrorStateSurface) {
  if (errorSurface === 'export_job_worker') return 'export_job_worker（正式背景匯出任務）'
  if (errorSurface === 'signed_url_delivery') return 'signed_url_delivery（正式下載連結交付）'
  return 'analytics_export_panel（圖表統計匯出區塊）'
}

function getRetryPolicyLabel(retryPolicy: AnalyticsExportErrorStateRetryPolicy) {
  if (retryPolicy === 'retry_after_provider_ready') {
    return 'retry_after_provider_ready（provider 可用後再重試）'
  }
  if (retryPolicy === 'no_retry_needed') return 'no_retry_needed（目前不需要重試）'
  return 'manual_retry_allowed（可由使用者重新點擊匯出）'
}

function getProviderStateLabel(providerState: string) {
  if (providerState === 'provider_not_configured') {
    return 'provider_not_configured（未啟用正式 provider）'
  }
  return `${providerState}（mock-safe provider state）`
}

function getAuditStateLabel(auditState: AnalyticsExportErrorStateAuditState) {
  if (auditState === 'pending_d1_audit_history') {
    return 'pending_d1_audit_history（正式 D1 audit history 待實作）'
  }
  return 'not_persisted_mock_safe（不寫入 D1 audit history）'
}

function assertSafeAnalyticsExportErrorStatePreview(value: unknown, path = 'input') {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      assertSafeAnalyticsExportErrorStatePreview(item, `${path}[${index}]`),
    )
    return
  }

  if (value && value instanceof Date) return

  if (value && typeof value === 'object') {
    for (const [key, nestedValue] of Object.entries(value)) {
      const normalizedKey = key.toLowerCase()
      if (forbiddenErrorStatePreviewKeys.has(normalizedKey)) {
        throw new Error(`Unsafe analytics export error state preview: forbidden key ${path}.${key}`)
      }
      assertSafeAnalyticsExportErrorStatePreview(nestedValue, `${path}.${key}`)
    }
    return
  }

  if (typeof value !== 'string') return

  if (unsafeErrorStateValuePatterns.some((pattern) => pattern.test(value))) {
    throw new Error(`Unsafe analytics export error state preview: forbidden value at ${path}`)
  }
}
